import React, { useState } from "react";
import { Badge, Button, Form, Table } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { Link, useParams, useRouteLoaderData } from "react-router-dom";

import { useAppApi, useAppContext } from "@/App";
import { useRequest } from "@components/AppHook";
import { DevForm } from "@components/Forms/Fields";
import { useSubmit } from "@components/Forms/Handlers";
import { RenderForm } from "@components/Forms/Render";
import { Awesome, DevModal } from "@components/Helpers";
import { CommonCard, ListingCard } from "@components/Pages";
import { url_reverse } from "@components/Utils";

export default function Community(props) {
    const params = useParams();
    if (params.id) {
        props = { ...props, id: params.id };
        return <Edit {...props} />;
    }
    return <List {...props} />;
}

function List({ gameId, header }) {
    const { t } = useTranslation();
    const { apiData, setApiData, apiUrl } = useAppApi(url_reverse("api:game:community:post:list", { game_id: gameId }));
    const { setDelData } = useAppContext();
    const [showSettingsModal, setShowSettingsModal] = useState(false);
    const [showUserSearchModal, setShowUserSearchModal] = useState(false);
    const { api_post } = useRequest();

    if (!apiData) return;

    const togglePublish = (ev, post_id) => {
        ev.preventDefault();
        api_post(null, apiUrl, {
            params: { ids: [post_id], action: "toggle_publish" },
            then: (resp) => {
                if (resp.status === "ok") {
                    setApiData(resp);
                }
            },
        });
    };

    const community_header = (
        <>
            {header}{" "}
            <a href={apiData.community_url} target="_blank">
                <Awesome type="external-link-square" />
            </a>
        </>
    );
    const headerTools = (
        <>
            <Button size="sm" variant="dark" onClick={() => setShowSettingsModal(true)}>
                <Awesome type="cog" />
            </Button>{" "}
            <Button size="sm" variant="dark" onClick={() => setShowUserSearchModal(true)}>
                <Awesome type="search" />
            </Button>{" "}
            <Link
                to={url_reverse("games:community:edit", { game_id: gameId, post_id: "add" })}
                className="btn btn-sm btn-dark"
            >
                <Awesome type="plus-circle" /> {t("Добавить")}
            </Link>
        </>
    );

    return (
        <ListingCard header={community_header} headerTools={headerTools}>
            <div>
                <Badge variant="success">
                    {t("Лайки")}: {apiData.stat?.likes}
                </Badge>{" "}
                <Badge variant="warning">
                    {t("Дизлайки")}: {apiData.stat?.dislikes}
                </Badge>{" "}
                <Badge variant="success">
                    {t("Показы")}: {apiData.stat?.views}
                </Badge>{" "}
                <Badge variant="warning">
                    {t("Клики")}: {apiData.stat?.clicks}
                </Badge>{" "}
                <Badge variant="primary">
                    {t("Комментарии")}: {apiData.stat?.comments_cnt}
                </Badge>{" "}
                <Badge variant="primary">
                    {t("Подписчики")}: {apiData.stat?.subscribers}
                </Badge>{" "}
            </div>

            <Table hover responsive className="dmg-table">
                <thead>
                    <tr>
                        <th>{t("Заголовок")}</th>
                        <th>{t("Дата")}</th>
                        <th>{t("Лайки")}</th>
                        <th>{t("Дизлайки")}</th>
                        <th>{t("Показы")}</th>
                        <th>{t("Клики")}</th>
                        <th>{t("Комментарии")}</th>
                        <th>{t("Локаль")}</th>
                        <th style={{ width: "10%" }}></th>
                    </tr>
                </thead>
                <tbody>
                    {apiData.object_list?.map((o, i) => {
                        return (
                            <tr key={`object_list${i}`}>
                                <td>
                                    <Link
                                        to={url_reverse("games:community:edit", {
                                            game_id: gameId,
                                            post_id: o.id,
                                        })}
                                    >
                                        {o.title}
                                    </Link>
                                </td>
                                <td>{o.date_added}</td>
                                <td>{o.likes || 0}</td>
                                <td>{o.dislikes || 0}</td>
                                <td>{o.views || 0}</td>
                                <td>{o.clicks || 0}</td>
                                <td>{o.comments_cnt || 0}</td>
                                <td>{o.lang}</td>
                                <td className="dmg-table-row-tools">
                                    <a href="#" onClick={(ev) => togglePublish(ev, o.id)}>
                                        {o.is_public ? (
                                            <Awesome type="toggle-on" tooltip={t("Снять с публикации")} />
                                        ) : (
                                            <Awesome type="toggle-off" color="black" tooltip={t("Опубликовать")} />
                                        )}
                                    </a>
                                    <a href={o.url} target="_blank" className="mx-2">
                                        <Awesome type="globe" />
                                    </a>
                                    <Awesome
                                        type="trash-o"
                                        color="red"
                                        onClick={() =>
                                            setDelData({
                                                url: url_reverse("api:game:community:post:edit", {
                                                    game_id: gameId,
                                                    post_id: o.id,
                                                }),
                                            })
                                        }
                                    />
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </Table>

            <SettingsModal showModal={showSettingsModal} setShowModal={setShowSettingsModal} />
            <UserSearchModal showModal={showUserSearchModal} setShowModal={setShowUserSearchModal} />
        </ListingCard>
    );
}

function Edit({ gameId, header }) {
    const params = useParams();
    const { t } = useTranslation();
    const { apiData } = useAppApi(url_reverse("api:game:community:post:edit", { game_id: gameId, post_id: params.id }));

    if (!apiData) return;

    const onOK = (resp) => {
        switch (resp.action) {
            case "preview":
                window.open(resp.object.url);
                window.history.replaceState(null, null, window.location.pathname);
                break;
            default:
                location.href = url_reverse("games:community", { game_id: gameId });
                break;
        }
    };

    const buttons = (
        <>
            <DevForm.Button variant="secondary" type="submit" name="action" value="draft">
                {t("Сохранить в черновик")}
            </DevForm.Button>{" "}
            <DevForm.Button variant="light" type="submit" name="action" value="preview">
                {t("Предпросмотр")}
            </DevForm.Button>{" "}
            <DevForm.Button variant="primary" type="submit" name="action" value="publish">
                {t("Опубликовать")}
            </DevForm.Button>
        </>
    );

    return (
        <CommonCard header={header}>
            <RenderForm buttons={buttons} afterRespOK={onOK} />
        </CommonCard>
    );
}

function SettingsModal({ showModal, setShowModal }) {
    const { t } = useTranslation();
    const onFormSubmit = useSubmit();

    const handleClose = () => {
        setShowModal(false);
    };

    return (
        <DevModal
            size="lg"
            show={showModal}
            onHide={handleClose}
            header={t("Настройки")}
            body={<RenderForm prefix="settings" bareForm={true} />}
            footer={
                <DevForm.Button variant="primary" type="submit" name="action" value="settings">
                    {t("Сохранить")}
                </DevForm.Button>
            }
            renderWrapper={(children) => (
                <Form onSubmit={(ev) => onFormSubmit(ev, { onOK: () => handleClose() })}>{children}</Form>
            )}
        />
    );
}

function UserSearchModal({ showModal, setShowModal }) {
    const { t } = useTranslation();
    const { apiData, setApiData } = useAppContext();
    const onFormSubmit = useSubmit();
    const gameCommonInfo = useRouteLoaderData("gameCommonInfo");

    const handleClose = () => {
        setShowModal(false);
    };

    const DrawResult = () => {
        if (apiData.sezam.project_ids?.includes(gameCommonInfo.game.sezam_project_id)) {
            return (
                <>
                    <div>
                        <strong>{t("Логин")}:</strong> {apiData.sezam.login}
                    </div>
                    <div>
                        <strong>User ID</strong>: {apiData.sezam.user_id}
                    </div>
                </>
            );
        }
        return t("Пользователь не найден");
    };

    const onOK = (resp) => {
        if (resp.sezam) {
            const newApiData = { ...apiData };
            newApiData.sezam = resp.sezam;
            setApiData(newApiData);
        }
    };

    return (
        <DevModal
            size="lg"
            show={showModal}
            onHide={handleClose}
            header={t("Поиск пользователей")}
            body={
                <>
                    <RenderForm prefix="user_search" bareForm={true} />
                    {apiData.sezam?.complete && <DrawResult />}
                </>
            }
            footer={
                <DevForm.Button variant="primary" type="submit" name="action" value="user_search">
                    {t("Искать")}
                </DevForm.Button>
            }
            renderWrapper={(children) => <Form onSubmit={(ev) => onFormSubmit(ev, { onOK })}>{children}</Form>}
        />
    );
}
